import { Grid, Typography } from '@mui/material';
import DocumentCivilUnionForm from 'civil-status/components/document-civil-union-form';
import { DocumentSearchCivilUnionCrumbs } from 'civil-status/constants/breadcrumb';
import createSchema from 'civil-status/utils/create-schema-civil-union';
import {
  transformDocumentCivilUnion,
  transformDocumentCivilUnionRequest,
} from 'civil-status/utils/transform-document-civil-union';
import { DocumentCivilUnionFormVM } from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import { useFormik } from 'formik';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentCivilUnionCM } from 'shell/models/document-civil-union';
import { HttpResponse } from 'shell/models/http-response';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import DescriptionForm from 'civil-status/components/shared/description-document';
import ButtonsForm from 'civil-status/components/shared/buttons';

const typeAct = 'U';

export default function DocumentCivilUnionFormPage(): React.ReactElement {
  const params = useParams();

  const { userProfile } = useUserProfile();

  const [civilUnion] = useHttpFetch<HttpResponse<DocumentCivilUnionCM>, DocumentCivilUnionFormVM>(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/visualizzazione-atto-unione-civile/${params.anno}-${params.numeroRegistro}-${params.numeroAtto}-${params.parte}-${params.serie1}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: transformDocumentCivilUnion,
    }
  );

  const updateCivilUnion = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/modifica-atto-unione-civile`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const substituteImage = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/sostituzione-immagine-atto-unione-civile`,
    {
      options: {
        method: 'PUT',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const [mode, setMode] = useState<PageMode>(PageMode.Detail);

  const validationSchema = useMemo(() => {
    return createSchema(mode);
  }, [mode]);

  const formikForm = useFormik<DocumentCivilUnionFormVM>({
    initialValues: civilUnion.data as DocumentCivilUnionFormVM,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values: DocumentCivilUnionFormVM, formContext) => {
      // If act substitution mode
      if (mode === PageMode.Substitution) {
        (
          substituteImage({
            data: {
              attoUnioneCivileForUploadVO: {
                numeroRegistro: values?.registerNumber || undefined,
                cognome1: values?.surname || undefined,
                cognome2: values?.surname2 || undefined,
                dichiarante: '',
                filename: values.uploadFile?.file?.name || undefined,
                matricola1: values?.matricola || undefined,
                matricola2: values?.matricola2 || undefined,
                nome1: values?.name || undefined,
                nome2: values?.name2 || undefined,
                anno: values?.year || undefined,
                numeroAtto: values?.documentNumber || undefined,
                parte: values?.portion || undefined,
                serie: `${values?.serie || ' '}${values?.serie2 || ''}` || undefined,
                idOperatore: userProfile.datiLogin?.key?.userName || undefined,
              },
              file: values.uploadFile?.rawTiff?.split(',')[1] || undefined,
              filename: values.uploadFile?.file?.name || undefined,
              mimeType: values.uploadFile?.file?.type || undefined,
            },
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');

          setMode(PageMode.Detail);

          formContext.setFieldValue(
            'indicatorUpdate',
            'È necessario effettuare la convalida dei dati.'
          );
        });
      }

      // If edit mode
      if (mode === PageMode.Edit) {
        const data = transformDocumentCivilUnionRequest(values);

        (
          updateCivilUnion({
            data,
          }) as ResponsePromise<void>
        ).then(() => {
          toast.success('Operazione eseguita con successo');

          setMode(PageMode.Detail);

          formContext.setFieldValue('indicatorUpdate', '');
        });
      }
    },
  });

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-unione-civile-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  useEffect(() => {
    return () => {
      if (params) {
        unlockDocument({
          data: {
            key: {
              numeroAtto: params.numeroAtto,
              numeroRegistro: params.numeroRegistro,
              parte: params.parte,
              serie1: params.serie1,
              anno: params.anno,
            },
          },
        });
      }
    };
  }, []);

  // visibility
  const [visibility, setVisibility] = useState(false);

  useEffect(() => {
    setVisibility(!!formikForm.values?.indicatorUpdate);
  }, [formikForm.values?.indicatorUpdate]);

  const deleteAll = useHttpClient(`${__CIVIL_STATUS_API_URL__}/annotazioni-atto/rimuovi/all`, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const handleDeleteAllInternational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: typeAct,
            numeroRegistro: formikForm.values?.registerNumber,
            anno: formikForm.values?.year,
            numeroAtto: formikForm.values?.documentNumber,
            parte: formikForm.values?.portion,
            serie1: formikForm.values?.serie,
          },
          tipoCertificato: 'INT',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      formikForm.setFieldValue('annotationInternationalList', []);
      setMode(PageMode.Detail);
    });
  }, [deleteAll, setMode, formikForm.values, typeAct]);

  const handleDeleteAllNational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: typeAct,
            numeroRegistro: formikForm.values?.registerNumber,
            anno: formikForm.values?.year,
            numeroAtto: formikForm.values?.documentNumber,
            parte: formikForm.values?.portion,
            serie1: formikForm.values?.serie,
          },
          tipoCertificato: 'NAZ',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      formikForm.setFieldValue('annotationNationalList', []);
      setMode(PageMode.Detail);
    });
  }, [formikForm.values, typeAct, deleteAll, setMode]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChanges = useCallback(
    (newValues) => {
      formikForm.setValues({
        ...formikForm.values,
        ...newValues,
      });
    },
    [formikForm.setValues, formikForm.values]
  );

  return (
    <>
      <Breadcrumbs
        items={[
          ...DocumentSearchCivilUnionCrumbs,
          { name: 'Atto di Unione Civile', path: '/stato-civile/unione-civile/ricerca/' },
        ]}
        base="stato-civile"
      />
      <form onSubmit={formikForm.handleSubmit}>
        <Grid container spacing={2}>
          <Grid container spacing={2} item xs={12} md={4}>
            <Grid item xs={12} md={6}>
              <Typography
                color="secondary.main"
                style={{
                  fontSize: '26px',
                }}
              >
                Atto di Unione Civile
                {mode === PageMode.International ? ' - Annotazioni Internazionali' : ''}
                {mode === PageMode.National ? ' - Annotazioni Italia' : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <DescriptionForm values={formikForm.values} />
            </Grid>
          </Grid>
          <Grid item xs={12} md={8}>
            <ButtonsForm
              mode={mode}
              onAnnotationInternationalMode={() => {
                setMode(PageMode.International);
              }}
              onAnnotationNationalMode={() => {
                setMode(PageMode.National);
              }}
              onDetailMode={() => {
                setMode(PageMode.Detail);
              }}
              onEditMode={() => {
                setMode(PageMode.Edit);
              }}
              onPrintMode={() => {
                setMode(PageMode.Print);
              }}
              onSubstitutionMode={() => {
                setMode(PageMode.Substitution);
              }}
              onAnnotationInternationalDeleteAll={handleDeleteAllInternational}
              onAnnotationNationalDeleteAll={handleDeleteAllNational}
              url="/stato-civile/unione-civile/ricerca"
            />
          </Grid>
        </Grid>
        <SearchMessages
          type={MessageType.Warning}
          title={formikForm.values?.indicatorUpdate ? `C'è 1 warning` : ''}
          messages={
            formikForm.values?.indicatorUpdate
              ? [{ id: 1, value: formikForm.values?.indicatorUpdate }]
              : []
          }
          visibility={visibility}
          onChangeVisibility={() => setVisibility(false)}
        />
        <DocumentCivilUnionForm
          onChange={formikForm.setFieldValue}
          onChanges={onChanges}
          values={formikForm.values}
          errors={formikForm.errors}
          touched={formikForm.touched}
          mode={mode}
          type={typeAct}
          setMode={setMode}
        />
      </form>
    </>
  );
}

DocumentCivilUnionFormPage.displayName = 'home page component';
